import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogs } from '../data/blogsData';

const BlogsPage = () => {
    const [activeCategory, setActiveCategory] = useState('All');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Extract unique categories
    const categories = useMemo(() => {
        const cats = ['All', ...new Set(blogs.map(blog => blog.category))];
        return cats;
    }, []);

    // Filtered blogs based on active category
    const filteredBlogs = useMemo(() => {
        if (activeCategory === 'All') return blogs;
        return blogs.filter(blog => blog.category === activeCategory);
    }, [activeCategory]);

    // Mock "Most Viewed" for sidebar (just picking a few)
    const mostViewed = useMemo(() => {
        return [...blogs].sort((a, b) => b.id - a.id).slice(0, 5);
    }, []);

    return (
        <div className="min-h-screen bg-[#F9F9F9] text-black font-nunito px-4 py-8 md:px-8 lg:py-12">
            <div className="max-w-5xl mx-auto">
                {/* Header Section */}
                {/* <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-black text-black mb-4 tracking-tight">Blogs</h1>
                    <p className="text-black/50 text-lg max-w-2xl mx-auto font-medium">
                        Your companion for smarter car care, maintenance, and vehicle safety.
                    </p>
                </div> */}

                {/* Category Bar - Horizontal Scroll on Mobile */}
                <div className="mb-8 flex overflow-x-auto pb-2 scrollbar-none gap-2 justify-start md:justify-center items-center">
                    <div className="inline-flex bg-[#F9F9F9]/70 backdrop-blur-md p-1.5 rounded-2xl border border-[#E6E6E6] shadow-sm">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-5 py-2 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                                    activeCategory === category
                                        ? 'bg-[#414141] text-white shadow-lg'
                                        : 'text-black/50 hover:text-black hover:bg-gray-100'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
                    
                    {/* Blogs List Column */}
                    <div className="space-y-6">
                        {filteredBlogs.length > 0 ? (
                            filteredBlogs.map((blog) => (
                                <Link 
                                    key={blog.id} 
                                    to={blog.path}
                                    className="group flex flex-col sm:flex-row bg-[#fafafa] rounded-[32px] border border-[#E6E6E6] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300"
                                >
                                    {/* Article Image - Smaller and Better */}
                                    <div className="relative w-full sm:w-60 md:w-72 h-44 sm:h-auto overflow-hidden flex-shrink-0">
                                        <img 
                                            src={blog.image} 
                                            alt={blog.title} 
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://via.placeholder.com/600x400?text=CarFido+Blog";
                                            }}
                                        />
                                        <div className="absolute top-3 left-3">
                                            <span className="px-3 py-1 bg-[#F9F9F9]/90 backdrop-blur-sm text-[10px] font-black uppercase tracking-widest text-black rounded-lg shadow-sm">
                                                {blog.category}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Article Content */}
                                    <div className="p-6 flex flex-col justify-between flex-1">
                                        <div>
                                            <div className="flex items-center gap-2 mb-2 text-[11px] font-bold text-gray-400">
                                                <span>{blog.date}</span>
                                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                                <span>{blog.readTime}</span>
                                            </div>
                                            <h2 className="text-base md:text-lg font-bold text-black group-hover:text-[#D81F26] transition-colors leading-tight mb-1.5 line-clamp-2">
                                                {blog.title}
                                            </h2>
                                            <p className="text-black/50 text-[13px] leading-relaxed line-clamp-2 sm:line-clamp-3">
                                                {blog.excerpt}
                                            </p>
                                        </div>
                                        <div className="mt-4 flex items-center justify-between">
                                            <span className="text-[12px] font-black text-black uppercase tracking-wider group-hover:translate-x-1 transition-transform inline-flex items-center gap-2 border-b-2 border-transparent group-hover:border-[#D81F26]">
                                                Read Article
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                    <polyline points="12 5 19 12 12 19"></polyline>
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <div className="text-center py-20 bg-[#F9F9F9] rounded-3xl border border-[#E6E6E6]">
                                <p className="text-gray-400 font-bold uppercase tracking-widest">No articles found in this category.</p>
                            </div>
                        )}
                    </div>

                    {/* Sidebar Column */}
                    <aside className="hidden lg:block space-y-8 h-fit sticky top-24">
                        <section className="bg-[#fafafa] rounded-3xl border border-[#E6E6E6] overflow-hidden shadow-sm p-6">
                            <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-6 border-b border-gray-100 pb-3">
                                Most Viewed Highly Read
                            </h3>
                            <div className="space-y-5">
                                {mostViewed.map((item) => (
                                    <Link key={item.id} to={item.path} className="group block">
                                        <div className="flex gap-4">
                                            <div className="w-16 h-12 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 border border-[#E6E6E6]">
                                                <img src={item.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" alt={item.title} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-bold text-black group-hover:text-[#D81F26] transition-colors line-clamp-2 leading-snug">
                                                    {item.title}
                                                </h4>
                                                <p className="text-[10px] text-gray-400 mt-1 font-bold">{item.category}</p>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>

                        <section className="bg-[#1F1F1F] rounded-3xl p-6 text-white overflow-hidden relative group">
                             <div className="absolute top-0 right-0 w-32 h-32 bg-[#F9F9F9]/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform" />
                            <div className="relative z-10">
                                <h3 className="text-lg font-black mb-2 tracking-tight">Need help now?</h3>
                                <p className="text-white/70 text-sm mb-6 leading-relaxed">
                                    Ask CarFido Assistant any car questions for personalized guides.
                                </p>
                                <Link to="/chat" className="inline-block bg-[#F9F9F9] text-black px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:scale-105 transition-transform">
                                    Launch Assistant
                                </Link>
                            </div>
                        </section>
                    </aside>
                </div>
            </div>

            {/* Scroll to Top */}
            <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="fixed bottom-8 right-8 w-12 h-12 bg-[#F9F9F9] border border-[#E6E6E6] rounded-full shadow-2xl flex items-center justify-center text-black hover:scale-110 active:scale-95 transition-all z-50 group"
                aria-label="Scroll to top"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-y-1 transition-transform">
                    <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
            </button>
        </div>
    );
};

export default BlogsPage;
